import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

const brand = {
  name: "Omniflow Labs",
  email: "hello@omniflowlabs.com",
  phoneDisplay: "8115843849",
  phoneHref: "+918115843849",
  mission:
    "Omniflow Labs builds Shopify apps, AI review software, conversion tools, and commerce automations for fast-growing ecommerce brands.",
};
const PUBLIC_API_BASE_URL = (
  process.env.REACT_APP_PUBLIC_API_BASE_URL || "https://api.omniflowlabs.tech"
).replace(/\/+$/, "");

const apps = [
  {
    name: "Quick Urgency Timer",
    status: "Live on Shopify",
    summary:
      "Launch-ready countdown timers, urgency strips, and conversion-focused layouts for product pages and storefront promotions.",
    points: ["Flexible design controls", "Storefront-ready widget", "Fast embedded admin workflow"],
    tags: ["Conversion", "Marketing"],
    cta: "View App",
  },
  {
    name: "ReviewMind",
    status: "Coming next",
    summary:
      "An AI review solution for Shopify with smarter collection, moderation, and response workflows for growing stores.",
    points: ["AI review intelligence", "Brand-safe moderation", "Built for long-term retention"],
    tags: ["Reviews", "AI"],
    cta: "Learn More",
  },
];

const featuredLaunches = [
  {
    eyebrow: "Featured launch",
    status: "Coming soon",
    title: "ReviewMind",
    summary: "AI-powered reviews that think, learn, rank, and convert for Shopify brands.",
    points: ["Smart review requests", "Sentiment & intent analysis", "Higher trust. Higher sales."],
    ctaLabel: "Explore ReviewMind",
    ctaHref: "#contact",
  },
  {
    eyebrow: "Live on Shopify",
    status: "Available now",
    title: "Quick Urgency Timer",
    summary: "Launch-ready countdown timers, urgency strips, and storefront conversion blocks for Shopify merchants.",
    points: ["Flexible design controls", "Fast embedded admin workflow", "Built for product and collection launches"],
    ctaLabel: "Explore Quick Urgency Timer",
    ctaHref: "#apps",
  },
  {
    eyebrow: "Roadmap",
    status: "Scaling next",
    title: "Omnichannel growth automations",
    summary: "HubSpot, Instagram, Facebook, and bot-driven lifecycle automations built from the same commerce engine.",
    points: ["Lead capture to CRM sync", "Social automation and reply flows", "Cross-platform conversion ops"],
    ctaLabel: "Plan your automation stack",
    ctaHref: "#contact",
  },
];

const launches = [
  {
    phase: "Now live",
    title: "Quick Urgency Timer",
    detail:
      "Countdown-driven urgency for merchants who need a polished launch page and a practical storefront conversion tool.",
  },
  {
    phase: "Next launch",
    title: "ReviewMind",
    detail:
      "A Shopify review stack with AI capabilities, designed to make review collection, display, and workflow management easier.",
  },
  {
    phase: "Expansion",
    title: "HubSpot, Instagram, Facebook, and bots",
    detail:
      "The holding company is set up for a broader suite of apps and automations across customer lifecycle channels.",
  },
];

const reviews = [
  {
    name: "James S.",
    role: "Founder, TrendHaus",
    quote:
      "Quick Urgency Timer is a game changer. Super easy to use and it actually increases conversions. Support is incredibly fast.",
  },
  {
    name: "Maria C.",
    role: "Marketing Manager, Glow Skin",
    quote:
      "We can already tell ReviewMind is going to be huge. The AI insights are exactly what our store needs to build more trust.",
  },
  {
    name: "Ryan L.",
    role: "Co-founder, Urban Edge",
    quote:
      "Omniflow Labs builds solid, reliable apps. Everything is smooth, well-documented and the team really cares.",
  },
];

const onboardingSteps = [
  {
    title: "1. Share the store goal",
    detail: "Tell us the product, launch date, and the workflow outcome you want first.",
  },
  {
    title: "2. Choose the app path",
    detail: "We map the Shopify app, landing page, or AI workflow into a launch plan.",
  },
  {
    title: "3. Ship and iterate",
    detail: "We launch, review early feedback, and keep improving the product surface.",
  },
];

const futurePlatforms = [
  { name: "HubSpot", detail: "CRM workflows and lead operations." },
  { name: "Instagram bots", detail: "Social automation and response flows." },
  { name: "Facebook bots", detail: "Messenger and support automation." },
  { name: "Other platforms", detail: "Expandable architecture for future launches." },
];

const faqItems = [
  {
    question: "What does Omniflow Labs do?",
    answer:
      "Omniflow Labs builds Shopify apps, AI review systems, and commerce automations for ecommerce brands that want faster launches and better conversion performance.",
  },
  {
    question: "Which Shopify app is live today?",
    answer:
      "Quick Urgency Timer is the live Shopify launch. It helps merchants deploy countdown timers, urgency strips, and promotion-focused storefront experiences quickly.",
  },
  {
    question: "What is ReviewMind?",
    answer:
      "ReviewMind is the next Omniflow Labs product: an AI-powered Shopify review solution designed for collection, moderation, sentiment analysis, and trust-building conversion workflows.",
  },
  {
    question: "Who should contact Omniflow Labs?",
    answer:
      "Shopify merchants, ecommerce operators, agencies, and growth teams that need app development, review AI, storefront conversion tooling, or automation across Shopify and adjacent platforms.",
  },
];

const aiNodes = ["shopify", "reviews", "ai", "crm", "social", "bots", "trust", "growth"];
const dataStreams = ["01", "AI", "CX", "ROI", "API", "BOT", "CRM", "UGC"];
const geometryShapes = ["commerce", "reviews", "launch", "platform"];
const bubbleField = ["spark", "trust", "signal", "launch", "growth", "flow", "loop", "orbit", "scale"];
const cosmicStars = Array.from({ length: 16 }, (_, index) => `star-${index + 1}`);
const cosmicGalaxies = ["commerce-core", "review-nebula"];
const cosmicPlanets = ["shopify", "reviews", "crm", "moon"];
const cosmicShips = ["scout", "launcher", "lander"];
const cosmicMissiles = ["signal-1", "signal-2"];
const cosmicSingularities = ["black-hole-alpha"];
const cosmicBursts = ["big-bang", "nova"];

function setMeta(name, value, attr = "name") {
  let tag = document.head.querySelector(`meta[${attr}="${name}"]`);
  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute(attr, name);
    document.head.appendChild(tag);
  }
  tag.setAttribute("content", value);
}

function setLink(rel, href) {
  let tag = document.head.querySelector(`link[rel="${rel}"]`);
  if (!tag) {
    tag = document.createElement("link");
    tag.setAttribute("rel", rel);
    document.head.appendChild(tag);
  }
  tag.setAttribute("href", href);
}

function useMarketingMetadata() {
  useEffect(() => {
    const canonicalUrl = "https://www.omniflowlabs.tech/";
    document.title = `${brand.name} | Shopify Apps, Shopify Review AI, and Ecommerce Automation`;
    setMeta(
      "description",
      "Omniflow Labs builds Shopify apps, Shopify review AI, urgency tools, and ecommerce automation systems for brands that want faster launches and higher conversion."
    );
    setMeta(
      "keywords",
      "Shopify apps, Shopify app development, Shopify review app, AI review software, ecommerce automation, Shopify conversion tools, urgency timer app"
    );
    setMeta("robots", "index,follow");
    setMeta("theme-color", "#07111f");
    setMeta("og:title", `${brand.name} | Shopify Apps, Shopify Review AI, and Ecommerce Automation`, "property");
    setMeta(
      "og:description",
      "Shopify apps, AI review software, and ecommerce automations built to launch faster and convert better.",
      "property"
    );
    setMeta("og:type", "website", "property");
    setMeta("og:url", canonicalUrl, "property");
    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:title", `${brand.name} | Shopify Apps, Shopify Review AI, and Ecommerce Automation`);
    setMeta(
      "twitter:description",
      "Shopify apps, AI review software, and ecommerce automations built to launch faster and convert better."
    );
    setLink("canonical", canonicalUrl);

    const websiteData = {
      "@context": "https://schema.org",
      "@type": "WebSite",
      name: brand.name,
      url: canonicalUrl,
      description: brand.mission,
    };
    const organizationData = {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: brand.name,
      url: canonicalUrl,
      email: brand.email,
      telephone: brand.phoneHref,
      description: brand.mission,
      areaServed: ["Global", "Shopify merchants", "SaaS teams"],
      contactPoint: {
        "@type": "ContactPoint",
        contactType: "sales",
        email: brand.email,
        telephone: brand.phoneHref,
        availableLanguage: ["English", "Hindi"],
      },
    };
    const applications = [
      {
        "@context": "https://schema.org",
        "@type": "SoftwareApplication",
        name: "Quick Urgency Timer",
        applicationCategory: "BusinessApplication",
        operatingSystem: "Web",
        description:
          "A Shopify countdown timer app for urgency-driven promotions and storefront launches.",
      },
      {
        "@context": "https://schema.org",
        "@type": "SoftwareApplication",
        name: "ReviewMind",
        applicationCategory: "BusinessApplication",
        operatingSystem: "Web",
        description:
          "An AI review solution for Shopify merchants with moderation and collection workflows.",
      },
    ];
    const faqData = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faqItems.map((item) => ({
        "@type": "Question",
        name: item.question,
        acceptedAnswer: {
          "@type": "Answer",
          text: item.answer,
        },
      })),
    };

    const id = "omniflow-jsonld";
    let script = document.getElementById(id);
    if (!script) {
      script = document.createElement("script");
      script.id = id;
      script.type = "application/ld+json";
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify([websiteData, organizationData, ...applications, faqData]);
  }, []);
}

function Icon({ name, className = "" }) {
  const common = {
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.7,
    strokeLinecap: "round",
    strokeLinejoin: "round",
    viewBox: "0 0 24 24",
    "aria-hidden": "true",
  };

  switch (name) {
    case "arrow":
      return (
        <svg className={className} {...common}>
          <path d="M5 12h13" />
          <path d="m13 6 6 6-6 6" />
        </svg>
      );
    case "spark":
      return (
        <svg className={className} {...common}>
          <path d="M12 2l1.8 6.2L20 10l-6.2 1.8L12 18l-1.8-6.2L4 10l6.2-1.8Z" />
          <path d="M19 16l.8 2.4L22 19l-2.2.6L19 22l-.8-2.4L16 19l2.2-.6Z" />
        </svg>
      );
    case "check":
      return (
        <svg className={className} {...common}>
          <path d="m5 12 4 4 10-10" />
        </svg>
      );
    case "star":
      return (
        <svg className={className} {...common}>
          <path d="m12 3 2.5 5.1 5.6.8-4 3.9.9 5.6-5-2.6-5 2.6.9-5.6-4-3.9 5.6-.8Z" />
        </svg>
      );
    case "mail":
      return (
        <svg className={className} {...common}>
          <path d="M4 7h16v10H4z" />
          <path d="m4 8 8 6 8-6" />
        </svg>
      );
    case "phone":
      return (
        <svg className={className} {...common}>
          <path d="M8.6 5.4 10.8 8a1 1 0 0 1-.1 1.3l-1.1 1.1a13.5 13.5 0 0 0 4 4l1.1-1.1A1 1 0 0 1 16 13l2.6 2.2a1 1 0 0 1 .1 1.4c-1 1.3-2.4 2.1-4 2-5.4-.2-10.4-5.2-10.6-10.6-.1-1.6.7-3 2-4a1 1 0 0 1 1.4.1Z" />
        </svg>
      );
    case "globe":
      return (
        <svg className={className} {...common}>
          <circle cx="12" cy="12" r="9" />
          <path d="M3 12h18" />
          <path d="M12 3c3 2.8 4.6 6 4.6 9s-1.6 6.2-4.6 9c-3-2.8-4.6-6-4.6-9S9 5.8 12 3Z" />
        </svg>
      );
    case "orbit":
      return (
        <svg className={className} {...common}>
          <path d="M4.5 12c2.4-4.5 6-7 9-7 3.5 0 6 2.5 6 6 0 3-2.5 6.6-7 9" />
          <path d="M12 7.2A4.8 4.8 0 1 1 7.2 12 4.8 4.8 0 0 1 12 7.2Z" />
        </svg>
      );
    case "shield":
      return (
        <svg className={className} {...common}>
          <path d="M12 3 19 6v5c0 4.8-2.8 8.5-7 10-4.2-1.5-7-5.2-7-10V6l7-3Z" />
          <path d="m9 12 2 2 4-4" />
        </svg>
      );
    case "trend":
      return (
        <svg className={className} {...common}>
          <path d="M4 16h16" />
          <path d="m6 13 4-4 3 3 5-6" />
          <path d="M15 6h3v3" />
        </svg>
      );
    default:
      return null;
  }
}

function SectionTitle({ eyebrow, title, copy }) {
  return (
    <div className="section-heading">
      <div className="section-heading__eyebrow">
        <span className="section-heading__line" />
        <span>{eyebrow}</span>
      </div>
      <h2>{title}</h2>
      <p>{copy}</p>
    </div>
  );
}

function AppCard({ app, delay }) {
  return (
    <article className="app-card" style={{ "--delay": delay }}>
      <div className="app-card__top">
        <div>
          <p className="app-card__status">{app.status}</p>
          <h3>{app.name}</h3>
        </div>
        <span className="app-card__orb" aria-hidden="true">
          <Icon name="orbit" />
        </span>
      </div>
      <p className="app-card__summary">{app.summary}</p>
      <ul className="feature-list">
        {app.points.map((point) => (
          <li key={point}>
            <Icon name="check" />
            <span>{point}</span>
          </li>
        ))}
      </ul>
      <div className="app-card__tags">
        {app.tags.map((tag) => (
          <span key={tag}>{tag}</span>
        ))}
      </div>
      <a className="text-link" href="#contact">
        {app.cta}
        <Icon name="arrow" className="text-link__icon" />
      </a>
    </article>
  );
}

function ReviewCard({ review, delay }) {
  return (
    <article className="review-card" style={{ "--delay": delay }}>
      <div className="review-card__stars" aria-label="Five star review">
        {[...Array(5)].map((_, index) => (
          <Icon key={index} name="star" />
        ))}
      </div>
      <p>{review.quote}</p>
      <div className="review-card__footer">
        <span className="review-card__avatar">{review.name.split(" ").map((part) => part[0]).join("")}</span>
        <div>
          <strong>{review.name}</strong>
          <span>{review.role}</span>
        </div>
      </div>
    </article>
  );
}

function PlatformCard({ platform, delay }) {
  return (
    <article className="platform-card" style={{ "--delay": delay }}>
      <span className="platform-card__mark">{platform.name.slice(0, 2)}</span>
      <div>
        <h3>{platform.name}</h3>
        <p>{platform.detail}</p>
      </div>
    </article>
  );
}

function FeaturedLaunchSlider() {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  useEffect(() => {
    if (isPaused) return undefined;

    const intervalId = window.setInterval(() => {
      setActiveIndex((current) => (current + 1) % featuredLaunches.length);
    }, 5600);

    return () => window.clearInterval(intervalId);
  }, [isPaused]);

  const activeSlide = featuredLaunches[activeIndex];

  return (
    <div
      className="launch-card launch-card--large launch-card--slider"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      onFocusCapture={() => setIsPaused(true)}
      onBlurCapture={() => setIsPaused(false)}
    >
      <div className="launch-card__top">
        <span className="launch-card__label">{activeSlide.eyebrow}</span>
        <span className="launch-card__pill">{activeSlide.status}</span>
      </div>
      <div className="launch-card__viewport" aria-live="polite">
        <div className="launch-card__slides" style={{ transform: `translateX(-${activeIndex * 100}%)` }}>
          {featuredLaunches.map((slide) => (
            <article key={slide.title} className="launch-card__slide">
              <div className="launch-card__center">
                <span className="launch-card__icon">
                  <Icon name="spark" />
                </span>
                <h2>{slide.title}</h2>
                <p>{slide.summary}</p>
                <ul>
                  {slide.points.map((point) => (
                    <li key={point}>
                      <Icon name="check" />
                      <span>{point}</span>
                    </li>
                  ))}
                </ul>
                <a className="button button--primary launch-card__cta" href={slide.ctaHref}>
                  {slide.ctaLabel}
                  <Icon name="arrow" />
                </a>
              </div>
            </article>
          ))}
        </div>
      </div>
      <div className="launch-card__dots" aria-label="Featured launches">
        {featuredLaunches.map((slide, index) => (
          <button
            key={slide.title}
            type="button"
            className={index === activeIndex ? "is-active" : ""}
            aria-label={`Show featured launch ${index + 1}`}
            aria-pressed={index === activeIndex}
            onClick={() => setActiveIndex(index)}
          />
        ))}
      </div>
    </div>
  );
}

function ContactForm({ compact = false }) {
  const [submissionState, setSubmissionState] = useState("idle");
  const [submissionMessage, setSubmissionMessage] = useState("");
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    project: "",
    message: "",
  });

  function updateField(event) {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
  }

  async function handleSubmit(event) {
    event.preventDefault();
    if (submissionState === "submitting") return;

    const payload = {
      name: form.name.trim(),
      email: form.email.trim(),
      company: form.company.trim(),
      project: form.project,
      message: form.message.trim(),
      sourceUrl: window.location.href,
      pagePath: window.location.pathname,
    };

    if (!payload.name || !payload.email || !payload.message) {
      setSubmissionState("error");
      setSubmissionMessage("Please add your name, email, and a short project brief.");
      return;
    }

    setSubmissionState("submitting");
    setSubmissionMessage("Sending your brief to Omniflow Labs...");

    try {
      const response = await fetch(`${PUBLIC_API_BASE_URL}/api/inquiries`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const responseBody = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(responseBody.error || "We could not send your project brief right now.");
      }

      setForm({
        name: "",
        email: "",
        company: "",
        project: "",
        message: "",
      });
      setSubmissionState("success");
      setSubmissionMessage("Thanks. Your inquiry has been saved and our team will review it next.");
    } catch (error) {
      setSubmissionState("error");
      setSubmissionMessage(
        error instanceof Error ? error.message : "We could not send your project brief right now."
      );
    }
  }

  return (
    <form
      className={`contact-form${compact ? " contact-form--compact" : ""}`}
      onSubmit={handleSubmit}
      noValidate
    >
      <div className="contact-form__grid">
        <label>
          <span>Name</span>
          <input
            name="name"
            value={form.name}
            onChange={updateField}
            placeholder="Your name"
            autoComplete="name"
            required
          />
        </label>
        <label>
          <span>Email</span>
          <input
            name="email"
            type="email"
            value={form.email}
            onChange={updateField}
            placeholder="you@company.com"
            autoComplete="email"
            required
          />
        </label>
        <label>
          <span>Company / store</span>
          <input
            name="company"
            value={form.company}
            onChange={updateField}
            placeholder="Store or brand"
            autoComplete="organization"
          />
        </label>
        <label>
          <span>Project type</span>
          <select name="project" value={form.project} onChange={updateField}>
            <option value="">Choose a focus</option>
            <option value="shopify-app">Shopify app</option>
            <option value="review-ai">Review AI</option>
            <option value="landing-page">Launch website</option>
            <option value="automation">Automation / bots</option>
          </select>
        </label>
        <label className="contact-form__message">
          <span>What do you want to ship?</span>
          <textarea
            name="message"
            value={form.message}
            onChange={updateField}
            rows="5"
            placeholder="Tell us the deadline, storefront goal, or automation outcome you need."
            required
          />
        </label>
      </div>
      <div className="contact-form__actions">
        <button type="submit" className="button button--primary" disabled={submissionState === "submitting"}>
          {submissionState === "submitting" ? "Sending..." : "Send project brief"}
        </button>
        <a href={`mailto:${brand.email}`} className="button button--ghost">
          <Icon name="mail" />
          {brand.email}
        </a>
        {!compact ? (
          <a href={`tel:${brand.phoneHref}`} className="button button--ghost" aria-label={`Call ${brand.phoneDisplay}`}>
            <Icon name="phone" />
            {brand.phoneDisplay}
          </a>
        ) : null}
      </div>
      <p
        className={`contact-form__status${submissionMessage ? " is-visible" : ""}${
          submissionState === "error" ? " is-error" : ""
        }`}
        role="status"
        aria-live={submissionState === "error" ? "assertive" : "polite"}
      >
        {submissionMessage}
      </p>
    </form>
  );
}

export default function PublicSite() {
  useMarketingMetadata();
  const mainRef = useRef(null);

  const nav = useMemo(
    () => [
      { label: "Home", href: "#home" },
      { label: "Apps", href: "#apps" },
      { label: "Launches", href: "#launches" },
      { label: "Reviews", href: "#reviews" },
      { label: "FAQ", href: "#faq" },
      { label: "Onboarding", href: "#onboarding" },
      { label: "Contact", href: "#contact" },
    ],
    []
  );
  const sectionClass = useCallback((baseClass) => `${baseClass} motion-section`, []);

  const updateMouseField = useCallback((event) => {
    const node = mainRef.current;
    if (!node) return;

    const mouseX = ((event.clientX / window.innerWidth) - 0.5) * 100;
    const mouseY = ((event.clientY / window.innerHeight) - 0.5) * 100;
    node.style.setProperty("--mouse-x", `${mouseX.toFixed(2)}px`);
    node.style.setProperty("--mouse-y", `${mouseY.toFixed(2)}px`);
    node.style.setProperty("--mouse-stage-x", `${(mouseX * -0.16).toFixed(2)}px`);
    node.style.setProperty("--mouse-stage-y", `${(mouseY * -0.12).toFixed(2)}px`);
    node.style.setProperty("--mouse-near-x", `${(mouseX * 0.34).toFixed(2)}px`);
    node.style.setProperty("--mouse-near-y", `${(mouseY * 0.26).toFixed(2)}px`);
    node.style.setProperty("--mouse-mid-x", `${(mouseX * 0.24).toFixed(2)}px`);
    node.style.setProperty("--mouse-mid-y", `${(mouseY * 0.18).toFixed(2)}px`);
    node.style.setProperty("--mouse-far-x", `${(mouseX * 0.12).toFixed(2)}px`);
    node.style.setProperty("--mouse-far-y", `${(mouseY * 0.1).toFixed(2)}px`);
    node.style.setProperty("--mouse-reverse-x", `${(mouseX * -0.16).toFixed(2)}px`);
    node.style.setProperty("--mouse-reverse-y", `${(mouseY * -0.12).toFixed(2)}px`);
  }, []);

  const resetMouseField = useCallback(() => {
    const node = mainRef.current;
    if (!node) return;
    node.style.setProperty("--mouse-x", "0px");
    node.style.setProperty("--mouse-y", "0px");
    node.style.setProperty("--mouse-stage-x", "0px");
    node.style.setProperty("--mouse-stage-y", "0px");
    node.style.setProperty("--mouse-near-x", "0px");
    node.style.setProperty("--mouse-near-y", "0px");
    node.style.setProperty("--mouse-mid-x", "0px");
    node.style.setProperty("--mouse-mid-y", "0px");
    node.style.setProperty("--mouse-far-x", "0px");
    node.style.setProperty("--mouse-far-y", "0px");
    node.style.setProperty("--mouse-reverse-x", "0px");
    node.style.setProperty("--mouse-reverse-y", "0px");
  }, []);

  useEffect(() => {
    const root = mainRef.current;
    if (!root) return undefined;

    const cards = Array.from(
      root.querySelectorAll(
        ".launch-card, .app-card, .timeline-card, .review-card, .step-card, .platform-card, .onboarding-panel"
      )
    );

    const motionVariants = ["rise", "left", "right", "orbit", "zoom", "tilt"];
    const getDocumentTop = (element) => {
      let top = 0;
      let node = element;
      while (node) {
        top += node.offsetTop || 0;
        node = node.offsetParent;
      }
      return top;
    };
    const activateCard = (card) => {
      if (card.classList.contains("card-motion--active")) return;
      card.classList.add("card-motion--active");
    };
    const updateSettledCardMotion = () => {
      const scrollY = window.scrollY || window.pageYOffset || 0;
      const viewportCenter = scrollY + window.innerHeight / 2;
      const headerSafeZone = 132;
      const dampDistance = 180;

      cards.forEach((card) => {
        if (!card.classList.contains("card-motion--settled")) return;
        const documentTop = getDocumentTop(card);
        const documentHeight = card.offsetHeight || 1;
        const documentBottom = documentTop + documentHeight;
        if (documentBottom < scrollY || documentTop > scrollY + window.innerHeight) return;

        const center = documentTop + documentHeight / 2;
        const viewportProgress = ((center - viewportCenter) / (window.innerHeight / 2));
        const clamped = Math.max(-1, Math.min(1, viewportProgress));
        const topDistance = documentTop - scrollY;
        const topDamp = Math.max(0, Math.min(1, (topDistance - headerSafeZone) / dampDistance));
        const direction = card.classList.contains("card-motion--left")
          ? -1
          : card.classList.contains("card-motion--right")
            ? 1
            : 0;

        card.style.setProperty("--card-scroll-y", `${(-clamped * 10 * topDamp).toFixed(2)}px`);
        card.style.setProperty("--card-scroll-x", `${(direction * clamped * 8 * topDamp).toFixed(2)}px`);
        card.style.setProperty("--card-scroll-rotate", `${(direction * clamped * -0.65 * topDamp).toFixed(2)}deg`);
      });
    };

    cards.forEach((card, index) => {
      card.classList.add("card-motion");
      card.classList.add(`card-motion--${motionVariants[index % motionVariants.length]}`);
      card.style.setProperty("--card-delay", `${(index % 4) * 70}ms`);
      card.style.setProperty("--card-scroll-x", "0px");
      card.style.setProperty("--card-scroll-y", "0px");
      card.style.setProperty("--card-scroll-rotate", "0deg");
    });

    if (typeof IntersectionObserver === "undefined") {
      cards.forEach((card) => card.classList.add("card-motion--active"));
      return undefined;
    }

    let scanFrame = null;
    const activateVisibleCards = () => {
      scanFrame = null;
      cards.forEach((card) => {
        const rect = card.getBoundingClientRect();
        if (rect.bottom > window.innerHeight * 0.18 && rect.top < window.innerHeight * 0.82) {
          activateCard(card);
        }
      });
      updateSettledCardMotion();
    };

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            activateCard(entry.target);
            observer.unobserve(entry.target);
          }
        });
      },
      { rootMargin: "-18% 0px -18% 0px", threshold: 0.18 }
    );

    cards.forEach((card) => observer.observe(card));
    window.requestAnimationFrame(activateVisibleCards);

    const onAnimationEnd = (event) => {
      if (!event.target.classList.contains("card-motion")) return;
      if (!event.animationName.startsWith("card")) return;
      event.target.classList.add("card-motion--settled");
      updateSettledCardMotion();
    };

    const onScroll = () => {
      if (scanFrame) return;
      scanFrame = window.requestAnimationFrame(activateVisibleCards);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    root.addEventListener("animationend", onAnimationEnd);

    return () => {
      observer.disconnect();
      window.removeEventListener("scroll", onScroll);
      root.removeEventListener("animationend", onAnimationEnd);
      if (scanFrame) {
        window.cancelAnimationFrame(scanFrame);
      }
    };
  }, []);

  return (
    <main
      ref={mainRef}
      className="marketing-site"
      id="home"
      onPointerMove={updateMouseField}
      onPointerLeave={resetMouseField}
    >
      <div className="marketing-site__background" aria-hidden="true">
        <span />
        <span />
        <span />
      </div>
      <div className="marketing-site__pulse" aria-hidden="true" />
      <div className="meta-world" aria-hidden="true">
        <div className="cosmic-stage">
          <div className="star-field">
            {cosmicStars.map((star, index) => (
              <span className="star-field__star" key={star} style={{ "--index": index }} />
            ))}
          </div>
          <div className="galaxy-field">
            {cosmicGalaxies.map((galaxy, index) => (
              <span className="galaxy-field__galaxy" data-galaxy={galaxy} key={galaxy} style={{ "--index": index }} />
            ))}
          </div>
          <div className="singularity-field">
            {cosmicSingularities.map((blackHole, index) => (
              <span className="singularity-field__black-hole" data-black-hole={blackHole} key={blackHole} style={{ "--index": index }} />
            ))}
          </div>
          <div className="planet-field">
            {cosmicPlanets.map((planet, index) => (
              <span className={`planet-field__body planet-field__body--${planet}`} data-planet={planet} key={planet} style={{ "--index": index }} />
            ))}
          </div>
          <div className="ship-field">
            {cosmicShips.map((ship, index) => (
              <span className={`ship-field__craft ship-field__craft--${ship}`} data-ship={ship} key={ship} style={{ "--index": index }} />
            ))}
          </div>
          <div className="missile-field">
            {cosmicMissiles.map((missile, index) => (
              <span className="missile-field__trail" data-missile={missile} key={missile} style={{ "--index": index }} />
            ))}
          </div>
          <div className="burst-field">
            {cosmicBursts.map((burst, index) => (
              <span className="burst-field__event" data-burst={burst} key={burst} style={{ "--index": index }} />
            ))}
          </div>
        </div>
        <div className="meta-grid" />
        <div className="ai-constellation">
          {aiNodes.map((node, index) => (
            <span className="ai-node" data-node={node} key={node} style={{ "--index": index }} />
          ))}
        </div>
        <div className="data-rain">
          {dataStreams.map((stream, index) => (
            <span key={stream} style={{ "--index": index }}>
              {stream}
            </span>
          ))}
        </div>
        <div className="geometry-field">
          {geometryShapes.map((shape, index) => (
            <span className={`geometry-field__shape geometry-field__shape--${shape}`} key={shape} style={{ "--index": index }} />
          ))}
        </div>
        <div className="bubble-field">
          {bubbleField.map((bubble, index) => (
            <span className="bubble-field__orb" data-bubble={bubble} key={bubble} style={{ "--index": index }} />
          ))}
        </div>
      </div>
      <header className="site-header">
        <a className="brand-mark" href="#home" aria-label={brand.name}>
          <span className="brand-mark__glyph">O</span>
          <span className="brand-mark__text">
            <strong>{brand.name}</strong>
            <small>Shopify apps, review AI, automation</small>
          </span>
        </a>
        <nav className="site-nav" aria-label="Primary">
          {nav.map((item) => (
            <a key={item.label} href={item.href}>
              {item.label}
            </a>
          ))}
        </nav>
        <a className="button button--small button--primary" href="#contact">
          Book a Demo
        </a>
      </header>

      <section className={sectionClass("hero", "hero")}>
        <div className="hero__copy">
          <div className="eyebrow">
            <Icon name="spark" />
            <span>Software studio &amp; product lab</span>
          </div>
          <h1>
            Building Shopify apps and AI commerce tools{" "}
            <span className="hero__accent">that drive real growth.</span>
          </h1>
          <p>
            {brand.name} builds Shopify apps, AI review software, urgency tooling, and ecommerce automation systems
            for brands that want faster launches, stronger trust, and higher conversion.
          </p>
          <div className="hero__actions">
            <a className="button button--primary" href="#apps">
              Explore Our Apps
              <Icon name="arrow" />
            </a>
            <a className="button button--ghost" href="#onboarding">
              Book a Demo
            </a>
          </div>
          <div className="hero__signals">
            <span>
              <Icon name="spark" />
              <strong>Performance first</strong>
              <small>Built for speed and reliability.</small>
            </span>
            <span>
              <Icon name="shield" />
              <strong>Trusted &amp; secure</strong>
              <small>Enterprise-grade standards.</small>
            </span>
            <span>
              <Icon name="trend" />
              <strong>Growth focused</strong>
              <small>Tools that increase conversions.</small>
            </span>
          </div>
        </div>

        <div className="hero__visual">
          <FeaturedLaunchSlider />
          <div className="hero__signal">
            <div>
              <Icon name="globe" />
              <span>Future platforms</span>
            </div>
            <strong>HubSpot · Instagram · Facebook · bots</strong>
          </div>
        </div>
      </section>

      <section className={sectionClass("section section--apps", "apps")} id="apps">
        <SectionTitle
          eyebrow="Latest apps"
          title="Shopify apps built for launch speed, conversion, and retention"
          copy="Our current roadmap starts with proven Shopify conversion tools, then expands into AI-powered review systems and broader ecommerce automations."
        />
        <div className="apps-grid">
          {apps.map((app, index) => (
            <AppCard key={app.name} app={app} delay={`${index * 0.12}s`} />
          ))}
        </div>
      </section>

      <section
        className={sectionClass("section section--launches", "launches")}
        id="launches"
      >
        <SectionTitle
          eyebrow="What's coming"
          title="Product launches and platform roadmap"
          copy="Every Omniflow Labs release is tied to a practical commerce problem: launch faster, convert better, or automate repetitive growth work."
        />
        <div className="launches-timeline">
          {launches.map((launch, index) => (
            <article key={launch.title} className="timeline-card" style={{ "--delay": `${index * 0.1}s` }}>
              <span className="timeline-card__phase">{launch.phase}</span>
              <h3>{launch.title}</h3>
              <p>{launch.detail}</p>
            </article>
          ))}
        </div>
      </section>

      <section
        className={sectionClass("section section--reviews", "reviews")}
        id="reviews"
      >
        <SectionTitle
          eyebrow="Loved by merchants"
          title="Merchant trust signals"
          copy="Social proof matters for SEO, conversion, and answer engines. We surface clear merchant outcomes, not vague testimonials."
        />
        <div className="reviews-grid">
          {reviews.map((review, index) => (
            <ReviewCard key={review.role} review={review} delay={`${index * 0.1}s`} />
          ))}
        </div>
      </section>

      <section
        className={sectionClass("section section--onboarding", "onboarding")}
        id="onboarding"
      >
        <SectionTitle
          eyebrow="Get started"
          title="Customer onboarding"
          copy="Share your store goal, pick the launch path, and move from brief to build without bloated discovery cycles."
        />
        <div className="onboarding-grid">
          <div className="onboarding-steps">
            {onboardingSteps.map((step) => (
              <article key={step.title} className="step-card">
                <h3>{step.title}</h3>
                <p>{step.detail}</p>
              </article>
            ))}
          </div>
          <div className="onboarding-panel">
            <div className="onboarding-panel__top">
              <span>Project intake</span>
              <strong>Start a new build</strong>
            </div>
            <ContactForm compact />
          </div>
        </div>
      </section>

      <section
        className={sectionClass("section section--platforms", "platforms")}
      >
        <SectionTitle
          eyebrow="The future"
          title="More platforms. More possibilities."
          copy="The Omniflow Labs stack expands from Shopify into CRM, social, and automation surfaces without losing the commerce-first execution model."
        />
        <div className="platforms-grid">
          {futurePlatforms.map((platform, index) => (
            <PlatformCard key={platform.name} platform={platform} delay={`${index * 0.09}s`} />
          ))}
        </div>
      </section>

      <section className={sectionClass("section section--faq", "faq")} id="faq">
        <SectionTitle
          eyebrow="Answer engine ready"
          title="Frequently asked questions about Omniflow Labs"
          copy="These direct answers help merchants, buyers, and AI answer engines understand what Omniflow Labs builds and who should work with us."
        />
        <div className="faq-grid">
          {faqItems.map((item) => (
            <article key={item.question} className="review-card faq-card">
              <h3>{item.question}</h3>
              <p>{item.answer}</p>
            </article>
          ))}
        </div>
      </section>

      <section
        className={sectionClass("section section--contact", "contact")}
        id="contact"
      >
        <SectionTitle
          eyebrow="Let's talk"
          title="Talk to Omniflow Labs"
          copy="Need a Shopify app, AI review workflow, or commerce automation system? Share the brief and we&apos;ll map the fastest launch path."
        />
        <div className="contact-grid">
          <div className="contact-copy">
            <div className="contact-copy__row">
              <Icon name="phone" />
              <div>
                <strong>Phone</strong>
                <p>
                  <a href={`tel:${brand.phoneHref}`} aria-label={`Call ${brand.phoneDisplay}`}>
                    {brand.phoneDisplay}
                  </a>
                </p>
              </div>
            </div>
            <div className="contact-copy__row">
              <Icon name="mail" />
              <div>
                <strong>Email</strong>
                <p>{brand.email}</p>
              </div>
            </div>
            <div className="contact-copy__row">
              <Icon name="globe" />
              <div>
                <strong>Scope</strong>
                <p>Shopify apps, review AI, conversion tooling, HubSpot, and social automation</p>
              </div>
            </div>
          </div>
          <ContactForm />
        </div>
      </section>

      <footer className="site-footer">
        <div>
          <strong>{brand.name}</strong>
          <p>{brand.mission}</p>
        </div>
        <div className="site-footer__links">
          <a href="#apps">Apps</a>
          <a href="#launches">Launches</a>
          <a href="#reviews">Reviews</a>
          <a href="#faq">FAQ</a>
          <a href="#contact">Contact</a>
        </div>
      </footer>
    </main>
  );
}
